import { zodResolver } from "@hookform/resolvers/zod";
import { Button, ErrorContainer, Form, FormControl, FormField, FormInput, FormItem, FormMessage, IconBin, IconInputSearch, Text } from "@sentricmusic/ui";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import type { SpotifyPlaylistInfoResponse, SpotifyTrackItem } from "@/features/songs/album/types/spotify";
import type { BackendTrackSummary } from "@/features/songs/spotify/backendMappers";

import { apiCall } from "@/api/shared";
import useMultiSongStore from "@/features/songs/album/store/multiSongStore";
import { useWizardStore } from "@/features/songs/album/store/wizardStore";
import { mapBackendTrack } from "@/features/songs/spotify/backendMappers";
import { useUIStateStore } from "@/store/uiStateStore";
import { isValidSpotifyPlaylistUrl } from "@/utils/spotifyUrlUtils";

/** Maximum number of tracks allowed from a single playlist. */
const MAX_PLAYLIST_TRACKS = 200;

type BackendPlaylist = {
  id: string;
  name: string;
  owner: string | null;
  totalTracks: number | null;
  spotifyUrl: string | null;
  description: string | null;
  isPublic: boolean | null;
  collaborative: boolean | null;
  images: { url: string; height: number | null; width: number | null }[];
  tracks: BackendTrackSummary[];
};

type BackendPlaylistResponse = { playlist: BackendPlaylist };

function mapBackendPlaylist(playlist: BackendPlaylist, tracks: SpotifyTrackItem[]): SpotifyPlaylistInfoResponse {
  return {
    id: playlist.id,
    name: playlist.name,
    description: playlist.description,
    owner: { id: "", display_name: playlist.owner ?? "" },
    collaborative: playlist.collaborative ?? undefined,
    public: playlist.isPublic,
    images: playlist.images,
    external_urls: playlist.spotifyUrl != null ? { spotify: playlist.spotifyUrl } : undefined,
    tracks: {
      items: tracks.map(track => ({
        added_at: null,
        added_by: null,
        is_local: false,
        track,
      })),
      href: "",
      limit: tracks.length,
      next: null,
      offset: 0,
      previous: null,
      total: tracks.length,
    },
  };
}

/**
 * Step 1 for the "Add Playlist" flow.
 *
 * Handles:
 * - Spotify playlist URL input + validation (react-hook-form + zod)
 * - Playlist + track fetch (including ISRCs) via backend SpotifyController
 * - Caps imported tracks at MAX_PLAYLIST_TRACKS
 * - Spotify embed iframe preview
 *
 * The parent orchestrator (SongsAddMultiple) renders WizardNavigation separately
 * and reads sourceData/tracks from multiSongStore to determine when to enable "Next".
 */
export default function PlaylistImportStep() {
  const { t } = useTranslation(["playlist", "album"]);
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { currentStep, markStepComplete, nextStep } = useWizardStore();
  const location = useRouterState({ select: s => s.location });
  const hasLoadedFromUrl = useRef(false);

  const {
    setSourceData,
    setSourceType,
    setTracks,
    setIsLoading,
    setError: setErrorMsg,
    isLoading,
    error: errorMsg,
  } = useMultiSongStore();

  // --- Local state ---
  const [spotifyResult, setSpotifyResult] = useState<SpotifyPlaylistInfoResponse | null>(null);
  const [showIframeSkeleton, setShowIframeSkeleton] = useState(false);

  // Ensure sourceType is set to "playlist" on mount
  useEffect(() => {
    setSourceType("playlist");
  }, [setSourceType]);

  // --- Zod form ---
  const spotifyPlaylistSchema = z.object({
    spotifyUrl: z
      .string()
      .min(1, t("playlist:enter-spotify-playlist-url", "Please enter a Spotify playlist URL"))
      .refine(
        url => isValidSpotifyPlaylistUrl(url),
        { message: t("playlist:enter-valid-spotify-playlist-url", "Please enter a valid Spotify playlist URL") },
      ),
  });

  type SpotifyPlaylistFormData = { spotifyUrl: string };

  const form = useForm<SpotifyPlaylistFormData>({
    resolver: zodResolver(spotifyPlaylistSchema),
    defaultValues: { spotifyUrl: "" },
  });

  // --- Iframe skeleton timer ---
  useEffect(() => {
    if (showIframeSkeleton) {
      const timer = setTimeout(() => setShowIframeSkeleton(false), 1800);
      return () => clearTimeout(timer);
    }
  }, [showIframeSkeleton]);

  // --- Helpers ---
  const parsePlaylistId = (url: string): string | null => {
    const urlParts = url.split("/");
    const playlistId = urlParts[urlParts.length - 1].split("?")[0];
    return playlistId && playlistId !== "" ? playlistId : null;
  };

  // --- Core fetch logic ---
  const fetchPlaylistData = async (url: string): Promise<boolean> => {
    if (!url || url.trim() === "")
      return false;

    setIsLoading(true);
    setErrorMsg(null);

    try {
      const playlistId = parsePlaylistId(url);

      if (playlistId == null) {
        throw new Error(t("playlist:could-not-parse-playlist-id", "Could not parse Playlist ID from URL."));
      }

      const response = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/spotify/playlist/${playlistId}`,
      }) as BackendPlaylistResponse;

      if (!response?.playlist?.id) {
        throw new Error("Backend returned unexpected response shape");
      }

      // Map backend tracks, capping at MAX_PLAYLIST_TRACKS
      const allTracks = response.playlist.tracks ?? [];
      const rawTracks = allTracks.slice(0, MAX_PLAYLIST_TRACKS);
      const truncated = allTracks.length > MAX_PLAYLIST_TRACKS;
      const tracks = rawTracks.map(mapBackendTrack);

      const playlistInfoData = mapBackendPlaylist(response.playlist, tracks);

      setSourceData(playlistInfoData);
      setSpotifyResult(playlistInfoData);
      setTracks(tracks);
      setShowIframeSkeleton(true);

      if (truncated) {
        toast.warning(
          t("playlist:playlist-truncated-message", {
            defaultValue: "This playlist has more than {{max}} tracks. Only the first {{max}} playable tracks have been imported.",
            max: MAX_PLAYLIST_TRACKS,
          }),
        );
      }

      return true;
    }
    catch (e) {
      console.error("Failed to fetch playlist data:", e);
      if (e instanceof Error) {
        setErrorMsg(t("playlist:fetch-playlist-error-with-message", { defaultValue: "Failed to fetch playlist data: {{message}}", message: e.message }));
      }
      else {
        setErrorMsg(t("playlist:fetch-playlist-error-unknown", "Failed to fetch playlist data due to an unknown error."));
      }
      setSourceData(null);
      setTracks([]);
      setSpotifyResult(null);
      return false;
    }
    finally {
      setIsLoading(false);
    }
  };

  // --- Deep-link: auto-fetch playlist from ?spotifyPlaylistId= ---
  useEffect(() => {
    if (spotifyResult || hasLoadedFromUrl.current || currentStep !== "import")
      return;

    const searchParams = new URLSearchParams(location.searchStr);
    const spotifyPlaylistId = searchParams.get("spotifyPlaylistId");
    if (spotifyPlaylistId == null || spotifyPlaylistId.trim() === "")
      return;

    hasLoadedFromUrl.current = true;

    const isValidSpotifyPlaylistId = /^[\da-z]{22}$/i.test(spotifyPlaylistId);
    if (!isValidSpotifyPlaylistId) {
      setErrorMsg(
        t("playlist:invalid-spotify-playlist-id", {
          defaultValue: "Invalid Spotify playlist ID. Expected 22 characters, got {{length}}. Please check the link or enter a Spotify playlist URL manually.",
          length: spotifyPlaylistId.length,
        }),
      );
      return;
    }

    const runDeepLinkFetch = async () => {
      const success = await fetchPlaylistData(`https://open.spotify.com/playlist/${spotifyPlaylistId}`);
      if (success) {
        markStepComplete("import");
        nextStep();
      }
    };
    void runDeepLinkFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.searchStr, spotifyResult, currentStep]);

  // --- Form submission ---
  const onFormSubmit = async (data: SpotifyPlaylistFormData) => {
    await fetchPlaylistData(data.spotifyUrl);
  };

  const removeSpotify = () => {
    setSpotifyResult(null);
    setSourceData(null);
    setTracks([]);
    setErrorMsg(null);
    setShowIframeSkeleton(false);
    form.reset();
  };

  return (
    <>
      <div
        className={`flex flex-col gap-6 shadow-md ${isTunecore ? "text-white md:px-14 md:py-10 p-4 rounded-lg bg-layer-inverse" : "bg-layer-raised p-4 md:px-14 md:py-10 rounded-lg text-ink-primary"}`}
        id="playlistSpotifyStep"
      >
        <div>
          <Text as="subtitle">
            {t("playlist:import-from-spotify-header", "Import from Spotify")}
          </Text>
          <Text>
            {t("playlist:spotify-playlist-import-description", "Paste a Spotify playlist URL below to import the songs. We'll fetch the track details so you can register them as works.")}
          </Text>
        </div>
        <div>
          {!spotifyResult && (
            <Form {...form}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void form.handleSubmit(onFormSubmit)(e);
                }}
                className="flex flex-col gap-4"
              >
                <FormField
                  control={form.control}
                  name="spotifyUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FormInput
                          type="text"
                          placeholder={t("playlist:paste-spotify-playlist-url", "Paste Spotify Playlist URL here")}
                          className="w-full font-medium"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  className={`self-end group ${
                    isTunecore
                      ? "text-white font-tunecore hover:text-ink-accent"
                      : "bg-[#1ED760] hover:bg-[#1DB954]  text-black"
                  }`}
                  type="submit"
                  variant="darkSecondary"
                  icon={<IconInputSearch className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "stroke-black"}`} />}
                  disabled={isLoading}
                  label={isLoading ? t("playlist:fetching-playlist", "Fetching playlist...") : t("playlist:fetch-spotify-playlist", "Fetch Spotify Playlist")}
                />
              </form>
            </Form>
          )}
          {spotifyResult && (
            <div className="flex flex-col gap-4">
              <div className="relative w-full min-h-[400px]">
                {showIframeSkeleton
                  ? (
                      <div className="w-full min-h-[400px] bg-black/10 backdrop-blur-xl rounded-lg animate-pulse flex flex-col">
                        <div className="flex items-center gap-4 p-6">
                          <div className="w-20 h-20 rounded-md bg-black/10 backdrop-blur-xl animate-pulse" />
                          <div className="flex-1">
                            <div className="w-3/4 h-4 mb-2 rounded-md bg-black/10 backdrop-blur-xl animate-pulse" />
                            <div className="w-1/2 h-3 rounded-md bg-black/10 backdrop-blur-xl animate-pulse" />
                          </div>
                        </div>
                        <div className="flex-1 p-6 pt-0">
                          <div className="space-y-6">
                            {Array.from({ length: 4 }, (_, index) => (
                              <div key={`track-skeleton-${index}`} className="flex items-center gap-4">
                                <div className="w-6 h-4 rounded bg-black/10 backdrop-blur-xl animate-pulse" />
                                <div className="flex-1 h-4 rounded bg-black/10 backdrop-blur-xl animate-pulse" />
                                <div className="w-12 h-4 rounded bg-black/10 backdrop-blur-xl animate-pulse" />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/20">
                          <div className="flex items-center gap-2 font-medium text-ink-primary">
                            <div className="w-4 h-4 border-2 border-current rounded-full border-t-transparent animate-spin" />
                            {t("album:loading-spotify-preview")}
                          </div>
                        </div>
                      </div>
                    )
                  : (
                      <iframe
                        title="preview"
                        src={`https://open.spotify.com/embed/playlist/${spotifyResult.id}`}
                        height="152"
                        allow="encrypted-media"
                        className="w-full min-h-[400px] rounded-xl"
                        sandbox="allow-scripts"
                      />
                    )}
              </div>

              <Button
                variant="darkSecondary"
                onClick={removeSpotify}
                className={`self-end group ${
                  isTunecore
                    ? "text-white font-tunecore hover:text-ink-accent"
                    : "bg-[#1ED760] hover:bg-[#1DB954]  text-black"
                }`}
                icon={<IconBin className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "stroke-black"}`} />}
                label={t("playlist:remove-selection", "Remove selection")}
              />
            </div>
          )}
        </div>
      </div>

      {errorMsg !== null && errorMsg !== "" && (
        <ErrorContainer
          className="mt-4"
          message={errorMsg}
        />
      )}
    </>
  );
}
